import type { SetStoreFunction } from "solid-js/store";
import { createStore } from "solid-js/store";
import type { ContextProviderComponent } from "solid-js";
import { createContext, untrack } from "solid-js";
import type { FormVersion } from "./formVersion";
import { initializedContext } from "../utils/initializedUtil";
import type FormId from "./FormId";
import type { SelectableIconName } from "../utils/iconUtil";
import type FormVersionId from "./FormVersionId";
import { object } from "valibot";
import { icon, optionalTextValue, requireText } from "../utils/validation/base";
import { MAX_CHARACTERS_MD, MAX_CHARACTERS_SM, SELECTABLE_FORM_ICONS } from "../constants";
import type { Theme } from "../types";

export type FormMeta = {
  description?: string;
  icon: SelectableIconName;
  theme: Theme;
  name: string;
  language: string;
  tags: string[];
};

export type Form = Record<string, unknown> & {
  id: FormId;
  meta: FormMeta;
  publishedVersion?: FormPublishedVersion;
  latestVersion?: FormLatestVersion;
  status: "ACTIVE" | "HIDDEN" | "TRASH" | "UNKNOWN";
};

export const formSchema = object({
  meta: object({
    name: requireText(MAX_CHARACTERS_SM),
    description: optionalTextValue(MAX_CHARACTERS_MD),
    icon: icon(SELECTABLE_FORM_ICONS),
  }),
});

export type FormPublishedVersion = {
  formVersionId: FormVersionId;
  formVersion?: FormVersion;
  publishedBy: string;
  publishedDate: Date;
};

export type FormLatestVersion = {
  id: FormVersionId;
  lastUpdated: Date;
  lastUpdatedBy: string;
  status: string;
};

type State = Form;

type Dispatch = SetStoreFunction<Form>;

const StateContext = createContext<State>();
const DispatchContext = createContext<Dispatch>();

export const FormProvider: ContextProviderComponent<Form> = (props) => {
  const [formStore, setFormStore] = createStore(untrack(() => props.value));

  return (
    <StateContext.Provider value={formStore}>
      <DispatchContext.Provider value={setFormStore}>{props.children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useFormState = () => initializedContext(StateContext, "useFormState", "FormProvider");
export const useFormDispatch = () => initializedContext(DispatchContext, "useFormDispatch", "FormProvider");
